import {
  CROSSBEAM_DEFAULT_PRESET_TYPE,
  CUSTOM_FIELD_SELECTION_LABEL,
  HIDDEN,
  OLAPS_ONLY,
  SINGLE_SHARE_PRESET_TYPE,
} from '@/constants/data_shares';
import { useDataSharePresetsStore } from '@/stores';
import type { DataSharePreset } from '@/types/data_share_presets';
import type { OutgoingSharingRule } from '@/types/data_shares';
import type { Population } from '@/types/populations';

export type PresetInfo = {
  presetId?: number | null;
  presetName: string;
  presetType?: DataSharePreset['preset_type'];
  fieldCount: number;
};

export default function useSharingRulePresetInfo() {
  const dataSharePresetsStore = useDataSharePresetsStore();

  /**
   * Gets preset info for a sharing rule (includes field count and preset name)
   */
  function getPresetInfoForRule(
    sharingRule: OutgoingSharingRule | undefined,
    population: Population,
  ): PresetInfo | null {
    if (!sharingRule) {
      return null;
    }

    const visibility = sharingRule.visibility;

    // No preset info for hidden or counts only
    if (visibility === HIDDEN || visibility === OLAPS_ONLY) {
      return null;
    }

    // Only for 'sharing' or 'greenfield_sharing' visibility
    const feedId = population.feed_id;
    const integrationType = population.integration_type;
    const isGoogleSheetOrCsv =
      integrationType === 'google_sheets' || integrationType === 'csv';
    const baseSourceId = isGoogleSheetOrCsv ? population.source_id : undefined;
    const presetId = sharingRule.data_share_presets_id;
    const preset = presetId
      ? dataSharePresetsStore.allPresets.find((p) => p.id === presetId)
      : undefined;
    const hasCustomFields = !!sharingRule.source_field_ids?.length;

    /* A rule with no preset row is implicitly on the Crossbeam preset, or on a
       custom field selection when it lists fields, so it has to carry that type
       or the Field Presets filter, which matches on type, drops it. */
    const presetType = presetId
      ? preset?.preset_type
      : hasCustomFields
        ? SINGLE_SHARE_PRESET_TYPE
        : CROSSBEAM_DEFAULT_PRESET_TYPE;

    /* The name follows the type: a custom field selection is stored as an
       unnamed single share preset, which getPresetName would otherwise label
       as the Crossbeam preset. */
    return {
      presetId,
      presetType,
      presetName:
        presetType === SINGLE_SHARE_PRESET_TYPE
          ? CUSTOM_FIELD_SELECTION_LABEL
          : presetId
            ? dataSharePresetsStore.getPresetName(
                feedId,
                baseSourceId,
                presetId,
              ) || 'Unknown Preset'
            : dataSharePresetsStore.crossbeamPresetLabel,
      fieldCount: sharingRule.source_field_ids?.length || 0,
    };
  }

  return { getPresetInfoForRule };
}
